/**
 * Company Commercial Term Constants
 *
 * Canonical value sets for `companies.contract_type` and `companies.payment_terms`
 * (text columns in Postgres — not PG enums, same as `companies.status`; see
 * `company-statuses.ts`). Both are `type: "select"` + `editable: true` in the
 * company field config, so the UI has always constrained them to a fixed list —
 * the list just lived inside a `"use client"`-adjacent options module that a
 * server-side Meridian tool cannot import (SERVER_CLIENT_IMPORT_BOUNDARY).
 *
 * This module is the single source of truth: the UI option arrays in
 * `components/ui/selects/options/contact.ts` are DERIVED from the tuples below,
 * and Meridian/MCP tools use `z.enum(COMPANY_CONTRACT_TYPE_VALUES)` — never a
 * hand-typed copy that drifts from the picker the humans use.
 *
 * ⚠️ CONTACTS USE A DIFFERENT CONTRACT-TYPE SET. `CONTACT_CONTRACT_TYPE_OPTIONS`
 * in `lib/schemas/contact.ts` is `hourly | monthly_retainer | project_based |
 * fixed_price | not_applicable` — same concept, different literals. Never feed a
 * contact contract type into a company field or vice versa.
 */

/** `companies.contract_type` — how the engagement is priced/shaped. */
export const COMPANY_CONTRACT_TYPE_VALUES = [
  "hourly",
  "monthly",
  "project",
  "fixed_price",
  "maintenance",
  "support",
  "marketing",
  "hosting",
  "implementation",
  "integration",
] as const;

export type CompanyContractType = (typeof COMPANY_CONTRACT_TYPE_VALUES)[number];

/** Display labels — the UI option array derives label+value from these. */
export const COMPANY_CONTRACT_TYPE_LABELS: Record<CompanyContractType, string> = {
  hourly: "Hourly",
  monthly: "Monthly Retainer",
  project: "Project-Based",
  fixed_price: "Fixed Price",
  maintenance: "Maintenance Contract",
  support: "Support Contract",
  marketing: "Marketing Services",
  hosting: "Hosting Services",
  implementation: "Implementation Services",
  integration: "Integration Services",
};

/** `companies.payment_terms` — invoice due-by terms. */
export const COMPANY_PAYMENT_TERMS_VALUES = [
  "net_15",
  "net_30",
  "net_45",
  "net_60",
  "due_on_receipt",
  "custom",
] as const;

export type CompanyPaymentTerms = (typeof COMPANY_PAYMENT_TERMS_VALUES)[number];

/** Display labels — the UI option array derives label+value from these. */
export const COMPANY_PAYMENT_TERMS_LABELS: Record<CompanyPaymentTerms, string> = {
  net_15: "Net 15",
  net_30: "Net 30",
  net_45: "Net 45",
  net_60: "Net 60",
  due_on_receipt: "Due on Receipt",
  custom: "Custom",
};
